import React, { useEffect, useState } from "react";
import { Button, Form, Dropdown } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaSearch,
  FaChevronDown,
  FaEllipsisV,
} from "react-icons/fa";
import TablePaginationBar from "../TablePaginationBar";
import DoctorActionsDropdown, {
  MENU_WIDTH,
  MENU_HEIGHT,
} from "./DoctorActionsDropdown";
import AddEditDoctorModal from "./AddEditDoctorModal";
import DeactivateDoctorModal from "./DeactivateDoctorModal";
import useClinicOptions from "../../hooks/useClinicOptions";

const doctorStatusColors = {
  Active: { bg: "#D0FAE5", color: "#006145" },
  Inactive: { bg: "#FEE2E2", color: "#991B1B" },
};

const StatusPill = ({ status }) => {
  const colors = doctorStatusColors[status] || doctorStatusColors.Active;
  return (
    <span
      style={{
        backgroundColor: colors.bg,
        color: colors.color,
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 600,
        display: "inline-block",
      }}
    >
      {status}
    </span>
  );
};

const DoctorsTab = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [clinicFilter, setClinicFilter] = useState("All Clinics");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [actionDoctor, setActionDoctor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const { clinics: activeClinics } = useClinicOptions(true);
  const { clinics: allClinics } = useClinicOptions(false);

  const clinicFilterOptions = allClinics.map((c) => c.name);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user_id");
      const params = new URLSearchParams();
      if (statusFilter !== "All") params.append("status", statusFilter);
      if (clinicFilter !== "All Clinics") params.append("clinic", clinicFilter);
      if (search.trim()) params.append("search", search.trim());

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-doctors?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success) {
        setDoctors(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchDoctors();
    }, search ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [statusFilter, clinicFilter, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, clinicFilter, rowsPerPage]);

  const totalPages = Math.ceil(doctors.length / rowsPerPage) || 1;
  const paginatedData = doctors.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const toggleDropdown = (id, e) => {
    e.stopPropagation();
    if (openDropdownId === id) {
      setOpenDropdownId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = MENU_WIDTH;
      const menuHeight = MENU_HEIGHT;
      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) {
        left = window.innerWidth - menuWidth - 8;
      }
      if (top + menuHeight > window.innerHeight - 8) {
        top = rect.top - menuHeight - 4;
      }
      if (top < 8) top = 8;
      setDropdownPos({ top, left });
      setOpenDropdownId(id);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".doctor-actions-menu") &&
        !event.target.closest(".actions-menu-btn")
      ) {
        setOpenDropdownId(null);
      }
    };
    if (openDropdownId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  const handleDoctorAction = async (action, doctor) => {
    setOpenDropdownId(null);

    switch (action) {
      case "edit":
        setActionDoctor(doctor);
        setShowEditModal(true);
        break;
      case "deactivate":
        setActionDoctor(doctor);
        setShowDeactivateModal(true);
        break;
      case "reactivate":
        try {
          const token = localStorage.getItem("user_id");
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/users/reactivate-doctor/${doctor.id}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (response.data?.success) {
            toast.success("Doctor reactivated successfully");
            await fetchDoctors();
          }
        } catch (error) {
          toast.error(
            error.response?.data?.message ||
              "Failed to reactivate doctor. Please try again.",
          );
        }
        break;
      default:
        break;
    }
  };

  const handleAddDoctor = async ({ name, clinic }) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/add-doctor`,
        { name, clinic },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowAddModal(false);
        toast.success("Doctor added successfully");
        await fetchDoctors();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to add doctor. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEditDoctor = async ({ name, clinic }) => {
    if (!actionDoctor) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-doctor/${actionDoctor.id}`,
        { name, clinic },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowEditModal(false);
        setActionDoctor(null);
        toast.success("Doctor updated successfully");
        await fetchDoctors();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to update doctor. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateDoctor = async () => {
    if (!actionDoctor) return;
    setDeactivating(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/deactivate-doctor/${actionDoctor.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setShowDeactivateModal(false);
        setActionDoctor(null);
        toast.success("Doctor deactivated successfully");
        await fetchDoctors();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to deactivate doctor. Please try again.",
      );
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <>
      <style>{`
        .doctors-table-container {
          width: 100%;
          overflow-x: auto;
        }
        .doctors-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .doctors-table th,
        .doctors-table td {
          white-space: nowrap;
          border-bottom: 1px solid #EAECEF;
          vertical-align: middle;
          padding: 14px 16px;
        }
        .doctors-table thead th {
          background-color: #064260;
          color: #ffffff;
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.6px;
          text-transform: uppercase;
        }
        .doctors-table tbody tr:nth-child(even) td {
          background-color: #F8FAFC;
        }
        .doctors-table tbody tr:hover td {
          background-color: #EBF3FF !important;
        }
      `}</style>

      <div
        className="bg-white rounded shadow-sm"
        style={{ width: "100%", display: "flex", flexDirection: "column" }}
      >
        <div className="app-page-toolbar">
          <h2 className="app-page-title">Doctors</h2>

          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div className="app-filter-search-wrap">
              <FaSearch className="app-filter-search-icon" />
              <Form.Control
                type="text"
                className="app-filter-search"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "220px" }}
              />
            </div>

            <Dropdown
              onSelect={(val) => {
                setClinicFilter(val);
                setCurrentPage(1);
              }}
            >
              <Dropdown.Toggle
                className="custom-dropdown-toggle app-filter-dropdown"
                style={{ width: "170px" }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {clinicFilter === "All Clinics" ? "Clinic" : clinicFilter}
                </span>
                <FaChevronDown
                  size={11}
                  style={{ opacity: 0.7, flexShrink: 0, marginLeft: "6px" }}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="All Clinics">All Clinics</Dropdown.Item>
                {clinicFilterOptions.map((clinic) => (
                  <Dropdown.Item key={clinic} eventKey={clinic}>
                    {clinic}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown
              onSelect={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
            >
              <Dropdown.Toggle
                className="custom-dropdown-toggle app-filter-dropdown"
                style={{ width: "120px" }}
              >
                {statusFilter === "All" ? "Status" : statusFilter}
                <FaChevronDown
                  size={11}
                  style={{ opacity: 0.7, flexShrink: 0, marginLeft: "6px" }}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="All">All</Dropdown.Item>
                <Dropdown.Item eventKey="Active">Active</Dropdown.Item>
                <Dropdown.Item eventKey="Inactive">Inactive</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button
              onClick={() => setShowAddModal(true)}
              style={{
                backgroundColor: "#27B99A",
                border: "none",
                borderRadius: "8px",
                height: "45px",
                padding: "0 24px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Add New
            </Button>
          </div>
        </div>

        <div className="doctors-table-container pb-2">
          <table className="doctors-table">
            <thead>
              <tr>
                <th style={{ minWidth: "80px" }}>ID</th>
                <th style={{ minWidth: "200px" }}>Doctor Name</th>
                <th style={{ minWidth: "220px" }}>Clinic</th>
                <th style={{ minWidth: "100px" }}>Status</th>
                <th style={{ minWidth: "90px" }}>Actions</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px", color: "#444" }}>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-5 text-muted">
                    Loading doctors...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-5 text-muted">
                    No doctors found
                  </td>
                </tr>
              ) : (
                paginatedData.map((doctor) => (
                  <tr key={doctor.id}>
                    <td style={{ fontFamily: "monospace", color: "#064260" }}>
                      {doctor.doctorId}
                    </td>
                    <td className="fw-semibold" style={{ color: "#064260" }}>
                      {doctor.name}
                    </td>
                    <td>{doctor.clinic}</td>
                    <td>
                      <StatusPill status={doctor.status} />
                    </td>
                    <td>
                      <div style={{ display: "inline-block" }}>
                        <button
                          type="button"
                          className="actions-menu-btn"
                          aria-label="Doctor actions"
                          aria-expanded={openDropdownId === doctor.id}
                          onClick={(e) => toggleDropdown(doctor.id, e)}
                          style={{
                            backgroundColor: "transparent",
                            border: "1px solid #27B99A",
                            color: "#27B99A",
                            borderRadius: "6px",
                            fontSize: "14px",
                            padding: "6px 10px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaEllipsisV size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <TablePaginationBar
          currentPage={currentPage}
          totalItems={doctors.length}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(n) => setRowsPerPage(n)}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>

      <DoctorActionsDropdown
        openDropdownId={openDropdownId}
        dropdownPos={dropdownPos}
        doctors={paginatedData}
        onAction={handleDoctorAction}
      />

      {showAddModal && (
        <AddEditDoctorModal
          clinics={activeClinics}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddDoctor}
          isSubmitting={saving}
        />
      )}

      {showEditModal && actionDoctor && (
        <AddEditDoctorModal
          doctor={actionDoctor}
          clinics={activeClinics}
          onClose={() => {
            setShowEditModal(false);
            setActionDoctor(null);
          }}
          onSave={handleEditDoctor}
          isSubmitting={saving}
        />
      )}

      {showDeactivateModal && actionDoctor && (
        <DeactivateDoctorModal
          doctor={actionDoctor}
          onClose={() => {
            setShowDeactivateModal(false);
            setActionDoctor(null);
          }}
          onConfirm={handleDeactivateDoctor}
          isSubmitting={deactivating}
        />
      )}
    </>
  );
};

export default DoctorsTab;
